import React, { useState, useEffect } from "react";
import {
  Dumbbell,
  Package,
  BookOpen,
  Settings2,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  XCircle,
  MoreHorizontal,
  Plus,
  Pencil,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Building2,
  CreditCard,
  HardDrive,
} from "lucide-react";
import { PlansTab, StoragePlansTab } from "./PlatformBilling";

import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Alert, AlertDescription } from "./ui/alert";
import { Skeleton } from "./ui/skeleton";
import { toast } from "sonner";

import { staticDataApi, ApiError } from "../utils/api";

const STATUS_BADGE = {
  active: "bg-green-100 text-green-800 gap-1",
  disabled: "bg-red-100 text-red-800 gap-1",
};

// Shared per-tab accent theme: a colored icon badge, a matching left-border
// accent for the card wrapping the table, and a gradient primary button —
// purely visual, reused across every Platform Data tab.
export const TAB_ACCENTS = {
  "static-data": {
    badge: "bg-slate-100 text-slate-600",
    border: "border-l-4 border-l-slate-400",
    button: "bg-gradient-to-r from-slate-500 to-slate-600 hover:from-slate-600 hover:to-slate-700 text-white border-0 shadow-sm",
  },
  sports: {
    badge: "bg-orange-100 text-orange-600",
    border: "border-l-4 border-l-orange-400",
    button: "bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white border-0 shadow-sm",
  },
  "org-types": {
    badge: "bg-purple-100 text-purple-600",
    border: "border-l-4 border-l-purple-400",
    button: "bg-gradient-to-r from-purple-500 to-fuchsia-500 hover:from-purple-600 hover:to-fuchsia-600 text-white border-0 shadow-sm",
  },
  packages: {
    badge: "bg-emerald-100 text-emerald-600",
    border: "border-l-4 border-l-emerald-400",
    button: "bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white border-0 shadow-sm",
  },
  programs: {
    badge: "bg-indigo-100 text-indigo-600",
    border: "border-l-4 border-l-indigo-400",
    button: "bg-gradient-to-r from-indigo-500 to-blue-500 hover:from-indigo-600 hover:to-blue-600 text-white border-0 shadow-sm",
  },
  plans: {
    badge: "bg-blue-100 text-blue-600",
    border: "border-l-4 border-l-blue-400",
    button: "bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white border-0 shadow-sm",
  },
  "storage-plans": {
    badge: "bg-pink-100 text-pink-600",
    border: "border-l-4 border-l-pink-400",
    button: "bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white border-0 shadow-sm",
  },
  coupons: {
    badge: "bg-amber-100 text-amber-600",
    border: "border-l-4 border-l-amber-400",
    button: "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white border-0 shadow-sm",
  },
} as const;

export function TabIconBadge({
  icon: Icon,
  accent,
}: {
  icon: React.ComponentType<{ className?: string }>;
  accent: keyof typeof TAB_ACCENTS;
}) {
  return (
    <span
      className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${TAB_ACCENTS[accent].badge}`}
    >
      <Icon className="h-5 w-5" />
    </span>
  );
}

export const formatBytes = (bytes: number) => {
  if (!bytes) return "0 B";

  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

export const formatDate = (dateValue: string | number | Date) => {
  if (!dateValue) return "—";

  return new Date(dateValue).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export function StatusBadge({ disabled }: { disabled: boolean }) {
  return (
    <Badge
      className={`gap-1 ${disabled ? STATUS_BADGE.disabled : STATUS_BADGE.active
        }`}
    >
      {disabled ? (
        <XCircle className="h-3 w-3" />
      ) : (
        <CheckCircle className="h-3 w-3" />
      )}
      {disabled ? "Disabled" : "Active"}
    </Badge>
  );
}

export function TableSkeleton({
  cols = 4,
  rows = 5,
}: {
  cols?: number;
  rows?: number;
}) {
  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              {Array.from({ length: cols }).map((_, index) => (
                <TableHead key={index}>
                  <Skeleton className="h-4 w-20" />
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>

          <TableBody>
            {Array.from({ length: rows }).map((_, rowIndex) => (
              <TableRow key={rowIndex}>
                {Array.from({ length: cols }).map((_, colIndex) => (
                  <TableCell key={colIndex}>
                    <Skeleton className="h-4 w-full" />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export function ErrorBanner({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <Alert variant="destructive">
      <AlertCircle className="h-4 w-4" />

      <AlertDescription className="flex items-center justify-between">
        <span>{message}</span>

        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setTimeout(() => onRetry(), 500);
          }}
        >
          <RefreshCw className="h-3 w-3 mr-1" />
          Retry
        </Button>
      </AlertDescription>
    </Alert>
  );
}

export function useApiData<T>(fetcher: () => Promise<any>) {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(true); // start true → show skeleton immediately, never flash empty state
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await fetcher();
      if (response?.success) {
        setData(response.data);
      } else {
        setError(response?.message || "Failed to load data");
      }
    } catch (err: any) {
      const message =
        err instanceof ApiError
          ? `${err.message} (${err.status})`
          : "Failed to load data";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await fetcher();
        if (cancelled) return;
        if (response?.success) {
          setData(response.data);
        } else {
          setError(response?.message || "Failed to load data");
        }
      } catch (err: any) {
        if (cancelled) return;
        const message =
          err instanceof ApiError
            ? `${err.message} (${err.status})`
            : "Failed to load data";
        setError(message);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return { data, isLoading, error, reload: load };
}

function SportsTab() {
  const { data, isLoading, error, reload } =
    useApiData<any>(staticDataApi.getSports);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState<any | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    name: "",
    disabled: false,
  });

  const sports: any[] = Array.isArray(data)
    ? data
    : Array.isArray(data?.sports)
      ? data.sports
      : [];

  const openAdd = () => {
    setEditItem(null);
    setForm({
      name: "",
      disabled: false,
    });
    setDialogOpen(true);
  };

  const openEdit = (sport: any) => {
    setEditItem(sport);
    setForm({
      name: sport.name ?? "",
      disabled: !!sport.disabled,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }

    setSaving(true);

    try {
      if (editItem) {
        await staticDataApi.updateSport(editItem._id, form);
        toast.success("Sport updated");
      } else {
        await staticDataApi.createSport(form);
        toast.success("Sport created");
      }

      setDialogOpen(false);
      reload();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Failed to save sport"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      await staticDataApi.deleteSport(deleteTarget._id);
      toast.success("Sport deleted permanently");
      setDeleteTarget(null);
      reload();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Failed to delete sport"
      );
    }
  };

  const handleToggleStatus = async (sport: any) => {
    try {
      await staticDataApi.toggleSportStatus(sport._id);
      toast.success(sport.disabled ? "Sport enabled" : "Sport disabled");
      reload();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Failed to update sport status"
      );
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <TabIconBadge icon={Dumbbell} accent="sports" />
          <div>
            <h2 className="text-lg font-semibold">Sports</h2>
            <p className="text-sm text-muted-foreground">
              All sports available on platform.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button size="sm" onClick={openAdd} className={TAB_ACCENTS.sports.button}>
            <Plus className="h-4 w-4 mr-2" />
            Add Sport
          </Button>
        </div>
      </div>

      {error && <ErrorBanner message={error} onRetry={reload} />}

      {isLoading ? (
        <TableSkeleton cols={3} />
      ) : (
        <Card className={TAB_ACCENTS.sports.border}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Sports Name</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {sports.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={3}
                    className="text-center py-10 text-muted-foreground"
                  >
                    No sports found.
                  </TableCell>
                </TableRow>
              ) : (
                sports.map((sport: any) => (
                  <TableRow key={sport._id}>
                    <TableCell className="font-medium">
                      {sport.name}
                    </TableCell>

                    <TableCell>
                      <StatusBadge disabled={!!sport.disabled} />
                    </TableCell>

                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>

                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuSeparator />

                          <DropdownMenuItem onClick={() => openEdit(sport)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>

                          <DropdownMenuItem onClick={() => handleToggleStatus(sport)}>
                            {sport.disabled ? (
                              <CheckCircle className="h-4 w-4 mr-2" />
                            ) : (
                              <XCircle className="h-4 w-4 mr-2" />
                            )}
                            {sport.disabled ? "Enable" : "Disable"}
                          </DropdownMenuItem>

                          <DropdownMenuSeparator />

                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => setDeleteTarget(sport)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete permanently
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editItem ? "Edit Sport" : "Add Sport"}</DialogTitle>
            <DialogDescription>
              {editItem
                ? "Update the sport details."
                : "Add a new sport to the platform."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label htmlFor="sport-name">Name</Label>
              <Input
                id="sport-name"
                value={form.name}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    name: event.target.value,
                  }))
                }
                placeholder="e.g. Basketball"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>

            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open: boolean) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Sport Permanently</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete{" "}
              <strong>{deleteTarget?.name}</strong>? This cannot be undone. If
              you just want to hide it, use Disable instead.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>

            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
            >
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function OrgTypesTab() {
  const { data, isLoading, error, reload } =
    useApiData<any>(staticDataApi.getOrganizationTypes);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState<any | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    organizationType: "",
    disabled: false,
  });

  const types: any[] = Array.isArray(data)
    ? data
    : Array.isArray(data?.organizationTypes)
      ? data.organizationTypes
      : Array.isArray(data?.types)
        ? data.types
        : [];

  const typeName = (type: any) =>
    type.organizationType || type.name || type.type || type.label || "—";

  const openAdd = () => {
    setEditItem(null);
    setForm({
      organizationType: "",
      disabled: false,
    });
    setDialogOpen(true);
  };

  const openEdit = (type: any) => {
    setEditItem(type);
    setForm({
      organizationType: typeName(type),
      disabled: !!type.disabled,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.organizationType.trim()) {
      toast.error("Name is required");
      return;
    }

    setSaving(true);

    try {
      if (editItem) {
        await staticDataApi.updateOrganizationType(editItem._id, form);
        toast.success("Organization type updated");
      } else {
        await staticDataApi.createOrganizationType(form);
        toast.success("Organization type created");
      }

      setDialogOpen(false);
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      await staticDataApi.deleteOrganizationType(deleteTarget._id);
      toast.success("Organization type deleted permanently");
      setDeleteTarget(null);
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to delete");
    }
  };

  const handleToggleStatus = async (type: any) => {
    try {
      await staticDataApi.toggleOrganizationTypeStatus(type._id);
      toast.success(type.disabled ? "Organization type enabled" : "Organization type disabled");
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to update status");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <TabIconBadge icon={Building2} accent="org-types" />
          <div>
            <h2 className="text-lg font-semibold">Organization Types</h2>
            <p className="text-sm text-muted-foreground">
              Types of organizations supported on the platform.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button size="sm" onClick={openAdd} className={TAB_ACCENTS["org-types"].button}>
            <Plus className="h-4 w-4 mr-2" />
            Add Type
          </Button>
        </div>
      </div>

      {error && <ErrorBanner message={error} onRetry={reload} />}

      {isLoading ? (
        <TableSkeleton cols={3} />
      ) : (
        <Card className={TAB_ACCENTS["org-types"].border}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Organization Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {types.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={3}
                    className="text-center py-10 text-muted-foreground"
                  >
                    No organization types found.
                  </TableCell>
                </TableRow>
              ) : (
                types.map((type: any) => (
                  <TableRow key={type._id}>
                    <TableCell className="font-medium">
                      {typeName(type)}
                    </TableCell>

                    <TableCell>
                      <StatusBadge disabled={!!type.disabled} />
                    </TableCell>

                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>

                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuSeparator />

                          <DropdownMenuItem onClick={() => openEdit(type)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>

                          <DropdownMenuItem onClick={() => handleToggleStatus(type)}>
                            {type.disabled ? (
                              <CheckCircle className="h-4 w-4 mr-2" />
                            ) : (
                              <XCircle className="h-4 w-4 mr-2" />
                            )}
                            {type.disabled ? "Enable" : "Disable"}
                          </DropdownMenuItem>

                          <DropdownMenuSeparator />

                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => setDeleteTarget(type)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete permanently
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editItem ? "Edit Organization Type" : "Add Organization Type"}
            </DialogTitle>
            <DialogDescription>
              {editItem
                ? "Update the organization type."
                : "Add a new organization type."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label htmlFor="org-type-name">Name</Label>
              <Input
                id="org-type-name"
                value={form.organizationType}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    organizationType: event.target.value,
                  }))
                }
                placeholder="e.g. School"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>

            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open: boolean) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Organization Type Permanently</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete{" "}
              <strong>{deleteTarget ? typeName(deleteTarget) : ""}</strong>?
              This cannot be undone. If you just want to hide it, use Disable
              instead.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>

            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
            >
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StaticKVTab() {
  const { data, isLoading, error, reload } =
    useApiData<any>(staticDataApi.getStaticData);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState<any | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    key: "",
    value: "",
  });

  const entries: any[] = Array.isArray(data)
    ? data
    : Array.isArray(data?.staticData)
      ? data.staticData
      : Array.isArray(data?.data)
        ? data.data
        : [];

  const openAdd = () => {
    setEditItem(null);
    setForm({
      key: "",
      value: "",
    });
    setDialogOpen(true);
  };

  const openEdit = (entry: any) => {
    setEditItem(entry);
    setForm({
      key: entry.key ?? "",
      value: entry.value ?? "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.key.trim()) {
      toast.error("Key is required");
      return;
    }

    setSaving(true);

    try {
      if (editItem) {
        await staticDataApi.updateStaticData(editItem._id, form);
        toast.success("Entry updated");
      } else {
        await staticDataApi.createStaticData(form);
        toast.success("Entry created");
      }

      setDialogOpen(false);
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      await staticDataApi.deleteStaticData(deleteTarget._id);
      toast.success("Entry deleted permanently");
      setDeleteTarget(null);
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to delete");
    }
  };

  const handleToggleStatus = async (entry: any) => {
    try {
      await staticDataApi.toggleStaticDataStatus(entry._id);
      toast.success(entry.disabled ? "Entry enabled" : "Entry disabled");
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to update status");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <TabIconBadge icon={Settings2} accent="static-data" />
          <div>
            <h2 className="text-lg font-semibold">Static Data</h2>
            <p className="text-sm text-muted-foreground">
              Platform-wide configuration key-value pairs.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button size="sm" onClick={openAdd} className={TAB_ACCENTS["static-data"].button}>
            <Plus className="h-4 w-4 mr-2" />
            Add Entry
          </Button>
        </div>
      </div>

      {error && <ErrorBanner message={error} onRetry={reload} />}

      {isLoading ? (
        <TableSkeleton cols={4} />
      ) : (
        <Card className={TAB_ACCENTS["static-data"].border}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Key</TableHead>
                <TableHead>Value</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {entries.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className="text-center py-10 text-muted-foreground"
                  >
                    No static data found.
                  </TableCell>
                </TableRow>
              ) : (
                entries.map((entry: any, index: number) => (
                  <TableRow key={entry._id || index}>
                    <TableCell>
                      <Badge variant="outline" className="font-mono text-xs">
                        {entry.key || "—"}
                      </Badge>
                    </TableCell>

                    <TableCell className="max-w-[400px] break-all text-sm">
                      {entry.value ?? "—"}
                    </TableCell>

                    <TableCell>
                      <StatusBadge disabled={!!entry.disabled} />
                    </TableCell>

                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>

                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuSeparator />

                          <DropdownMenuItem onClick={() => openEdit(entry)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>

                          <DropdownMenuItem onClick={() => handleToggleStatus(entry)}>
                            {entry.disabled ? (
                              <CheckCircle className="h-4 w-4 mr-2" />
                            ) : (
                              <XCircle className="h-4 w-4 mr-2" />
                            )}
                            {entry.disabled ? "Enable" : "Disable"}
                          </DropdownMenuItem>

                          <DropdownMenuSeparator />

                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => setDeleteTarget(entry)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete permanently
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editItem ? "Edit Entry" : "Add Entry"}</DialogTitle>
            <DialogDescription>
              {editItem
                ? "Update the key-value entry."
                : "Add a new configuration key-value pair."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label htmlFor="kv-key">Key</Label>
              <Input
                id="kv-key"
                value={form.key}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    key: event.target.value,
                  }))
                }
                placeholder="e.g. max_users"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="kv-value">Value</Label>
              <Input
                id="kv-value"
                value={form.value}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    value: event.target.value,
                  }))
                }
                placeholder="e.g. 100"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>

            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open: boolean) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Entry Permanently</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete the key{" "}
              <strong>{deleteTarget?.key}</strong>? This cannot be undone. If
              you just want to hide it, use Disable instead.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>

            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
            >
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function PackagesTab() {
  const [currentPage, setCurrentPage] = useState(1);
  const [packages, setPackages] = useState<any[]>([]);
  const [pagination, setPagination] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState<any | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    packageName: "",
    description: "",
    sport: "",
    price: "",
    numCritiques: "",
    disabled: false,
  });

  const load = async (page = 1) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await staticDataApi.getPackages({
        page,
        limit: 12,
      });

      if (response.success) {
        const raw = response.data;

        setPackages(Array.isArray(raw) ? raw : raw?.packages ?? []);
        setPagination(raw?.pagination ?? null);
      } else {
        setError(response.message || "Failed to load packages");
      }
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `${err.message} (${err.status})`
          : "Failed to load packages";

      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load(currentPage);
  }, [currentPage]);

  const totalItems = pagination?.totalItems ?? packages.length;
  const totalPages = pagination?.totalPages ?? 1;

  const priceDisplay = (pkg: any) => {
    const value = pkg.price;

    if (value == null) return "—";
    if (value === 0) return "Free";

    return `$${Number(value).toFixed(2)}`;
  };

  const coachName = (pkg: any) => {
    if (pkg.coachFirstName || pkg.coachLastName) {
      return `${pkg.coachFirstName ?? ""} ${pkg.coachLastName ?? ""}`.trim();
    }

    return null;
  };

  const ownerName = (pkg: any) => pkg.organizationName || coachName(pkg);

  const openEdit = (pkg: any) => {
    setEditItem(pkg);
    setForm({
      packageName: pkg.packageName || "",
      description: pkg.description || "",
      sport: pkg.sport || "",
      price: pkg.price != null ? String(pkg.price) : "",
      numCritiques: pkg.numCritiques != null ? String(pkg.numCritiques) : "",
      disabled: !!pkg.disabled,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!editItem) return;

    if (!form.packageName.trim()) {
      toast.error("Package name is required");
      return;
    }

    setSaving(true);

    try {
      const payload: any = {
        packageName: form.packageName.trim(),
        description: form.description.trim(),
        sport: form.sport.trim(),
        disabled: form.disabled,
      };

      if (form.price !== "") {
        payload.price = Number(form.price);
      }

      if (form.numCritiques !== "") {
        payload.numCritiques = Number(form.numCritiques);
      }

      const response = await staticDataApi.updatePackage(editItem._id, payload);

      if (response.success) {
        toast.success("Package updated");
        setDialogOpen(false);
        load(currentPage);
      } else {
        toast.error(response.message || "Save failed");
      }
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Failed to save package"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      const response = await staticDataApi.deletePackage(deleteTarget._id);

      if (response.success) {
        toast.success("Package deleted permanently");
        setDeleteTarget(null);
        load(currentPage);
      } else {
        toast.error(response.message || "Delete failed");
      }
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Failed to delete package"
      );
    }
  };

  const handleToggleStatus = async (pkg: any) => {
    try {
      const response = await staticDataApi.togglePackageStatus(pkg._id);
      if (response.success) {
        toast.success(pkg.disabled ? "Package enabled" : "Package disabled");
        load(currentPage);
      } else {
        toast.error(response.message || "Failed to update status");
      }
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Failed to update package status"
      );
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <TabIconBadge icon={Package} accent="packages" />
          <div>
            <h2 className="text-lg font-semibold">Packages</h2>
            <p className="text-sm text-muted-foreground">
              All packages available on the platform — {totalItems} total.
            </p>
          </div>
        </div>

      </div>

      {error && (
        <ErrorBanner message={error} onRetry={() => load(currentPage)} />
      )}

      {isLoading ? (
        <TableSkeleton cols={10} />
      ) : (
        <Card className={TAB_ACCENTS.packages.border}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>#</TableHead>
                <TableHead>Package</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Sport</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Critiques</TableHead>
                <TableHead>Views</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Updated</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {packages.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={10}
                    className="text-center py-10 text-muted-foreground"
                  >
                    No packages found.
                  </TableCell>
                </TableRow>
              ) : (
                packages.map((pkg: any, index: number) => {
                  const coach = coachName(pkg);
                  const owner = ownerName(pkg);
                  const price = priceDisplay(pkg);

                  return (
                    <TableRow key={pkg._id || index}>
                      <TableCell className="text-muted-foreground text-sm">
                        {(currentPage - 1) * 12 + index + 1}
                      </TableCell>

                      <TableCell>
                        <div className="flex items-center gap-3">
                          {pkg.coachProfileImageUrl ? (
                            <img
                              src={pkg.coachProfileImageUrl}
                              alt={coach ?? "Coach"}
                              className="h-10 w-10 rounded-full object-cover shrink-0"
                              onError={(event) => {
                                (
                                  event.currentTarget as HTMLImageElement
                                ).style.display = "none";
                              }}
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center shrink-0">
                              <Package className="h-4 w-4 text-muted-foreground/40" />
                            </div>
                          )}

                          <div>
                            <p className="font-medium leading-tight line-clamp-1">
                              {pkg.packageName || "Unnamed"}
                            </p>

                            {pkg.description && (
                              <p className="text-xs text-muted-foreground line-clamp-1 max-w-[200px]">
                                {pkg.description}
                              </p>
                            )}

                            {coach && (
                              <p className="text-xs text-muted-foreground line-clamp-1 max-w-[200px]">
                                {coach}
                                {pkg.coachEmail ? ` · ${pkg.coachEmail}` : ""}
                              </p>
                            )}

                            {pkg.paymentNotConfigured && (
                              <Badge
                                variant="destructive"
                                className="text-xs mt-0.5"
                              >
                                Payment not configured
                              </Badge>
                            )}
                          </div>
                        </div>
                      </TableCell>

                      <TableCell>
                        {owner ? (
                          <div>
                            <p className="text-sm line-clamp-1 max-w-[160px]">{owner}</p>
                            {pkg.organizationName && (
                              <Badge variant="secondary" className="text-xs mt-0.5">
                                Organization
                              </Badge>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>

                      <TableCell>
                        {pkg.sport ? (
                          <Badge variant="secondary" className="text-xs">
                            {pkg.sport}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>

                      <TableCell>
                        <span
                          className={`text-sm font-medium ${price === "Free" ? "text-green-600" : ""
                            }`}
                        >
                          {price}
                        </span>
                      </TableCell>

                      <TableCell className="text-sm text-muted-foreground">
                        {pkg.numCritiques != null ? pkg.numCritiques : "—"}
                      </TableCell>

                      <TableCell className="text-sm text-muted-foreground">
                        {pkg.views != null ? pkg.views.toLocaleString() : "—"}
                      </TableCell>

                      <TableCell>
                        <StatusBadge disabled={!!pkg.disabled} />
                      </TableCell>

                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {pkg.updatedAt ? formatDate(pkg.updatedAt) : "—"}
                      </TableCell>

                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>

                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            <DropdownMenuSeparator />

                            <DropdownMenuItem onClick={() => handleToggleStatus(pkg)}>
                              {pkg.disabled ? (
                                <CheckCircle className="h-4 w-4 mr-2" />
                              ) : (
                                <XCircle className="h-4 w-4 mr-2" />
                              )}
                              {pkg.disabled ? "Enable" : "Disable"}
                            </DropdownMenuItem>

                            <DropdownMenuSeparator />

                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => setDeleteTarget(pkg)}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete permanently
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Card>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <p className="text-sm text-muted-foreground">
            Page {currentPage} of {totalPages} — {totalItems} packages
          </p>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>

            <Button
              variant="outline"
              size="sm"
              disabled={currentPage >= totalPages}
              onClick={() =>
                setCurrentPage((page) => Math.min(totalPages, page + 1))
              }
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editItem ? "Edit Package" : "Add Package"}
            </DialogTitle>
            <DialogDescription>
              {editItem ? "Update the package details." : "Create a new package."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label htmlFor="pkg-name">Package Name</Label>
              <Input
                id="pkg-name"
                value={form.packageName}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    packageName: event.target.value,
                  }))
                }
                placeholder="e.g. Track Training Basic"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="pkg-desc">Description</Label>
              <Input
                id="pkg-desc"
                value={form.description}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    description: event.target.value,
                  }))
                }
                placeholder="Short description"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="pkg-sport">Sport</Label>
              <Input
                id="pkg-sport"
                value={form.sport}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    sport: event.target.value,
                  }))
                }
                placeholder="e.g. Track and Field"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="pkg-price">Price ($)</Label>
              <Input
                id="pkg-price"
                type="number"
                min="0"
                step="0.01"
                value={form.price}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    price: event.target.value,
                  }))
                }
                placeholder="0 for free"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="pkg-critiques">Number of Critiques</Label>
              <Input
                id="pkg-critiques"
                type="number"
                min="0"
                value={form.numCritiques}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    numCritiques: event.target.value,
                  }))
                }
                placeholder="e.g. 1"
              />
            </div>

          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>

            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open: boolean) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Package Permanently</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete{" "}
              <strong>{deleteTarget?.packageName || "this package"}</strong>?
              This cannot be undone. If you just want to hide it, use Disable
              instead.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>

            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
            >
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ProgramsTab() {
  const [currentPage, setCurrentPage] = useState(1);
  const [programs, setPrograms] = useState<any[]>([]);
  const [pagination, setPagination] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState<any | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    name: "",
    description: "",
    status: "draft",
    totalWeeks: "",
    disabled: false,
  });

  const load = async (page = 1) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await staticDataApi.getPrograms({
        page,
        limit: 10,
      });

      if (response.success) {
        const raw = response.data;

        setPrograms(Array.isArray(raw) ? raw : raw?.programs ?? []);
        setPagination(raw?.pagination ?? null);
      } else {
        setError(response.message || "Failed to load programs");
      }
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `${err.message} (${err.status})`
          : "Failed to load programs";

      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load(currentPage);
  }, [currentPage]);

  const totalItems = pagination?.totalItems ?? programs.length;
  const totalPages = pagination?.totalPages ?? 1;

  const openEdit = (program: any) => {
    setEditItem(program);
    setForm({
      name: program.name || "",
      description: program.description || "",
      status: program.status || "draft",
      totalWeeks: program.totalWeeks != null ? String(program.totalWeeks) : "",
      disabled: !!program.disabled,
    });
    setDialogOpen(true);
  };

  const ownerName = (program: any) => {
    if (program.organizationName) return program.organizationName;
    if (program.creatorFirstName || program.creatorLastName) {
      return `${program.creatorFirstName ?? ""} ${program.creatorLastName ?? ""}`.trim();
    }
    return null;
  };

  const handleSave = async () => {
    if (!editItem) return;

    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }

    setSaving(true);

    try {
      const payload: any = {
        name: form.name.trim(),
        description: form.description.trim(),
        status: form.status,
        disabled: form.disabled,
      };

      if (form.totalWeeks !== "") {
        payload.totalWeeks = Number(form.totalWeeks);
      }

      const response = await staticDataApi.updateProgram(editItem._id, payload);

      if (response.success) {
        toast.success("Program updated");
        setDialogOpen(false);
        load(currentPage);
      } else {
        toast.error(response.message || "Save failed");
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      const response = await staticDataApi.deleteProgram(deleteTarget._id);

      if (response.success) {
        toast.success("Program deleted permanently");
        setDeleteTarget(null);
        load(currentPage);
      } else {
        toast.error(response.message || "Delete failed");
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Delete failed");
    }
  };

  const handleToggleStatus = async (program: any) => {
    try {
      const response = await staticDataApi.toggleProgramStatus(program._id);
      if (response.success) {
        toast.success(program.disabled ? "Program enabled" : "Program disabled");
        load(currentPage);
      } else {
        toast.error(response.message || "Failed to update status");
      }
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Failed to update program status"
      );
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <TabIconBadge icon={BookOpen} accent="programs" />
          <div>
            <h2 className="text-lg font-semibold">Programs</h2>
            <p className="text-sm text-muted-foreground">
              All programs configured on the platform — {totalItems} total.
            </p>
          </div>
        </div>

      </div>

      {error && (
        <ErrorBanner message={error} onRetry={() => load(currentPage)} />
      )}

      {isLoading ? (
        <TableSkeleton cols={10} />
      ) : (
        <Card className={TAB_ACCENTS.programs.border}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>#</TableHead>
                <TableHead>Program</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Level</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Weeks</TableHead>
                <TableHead>Date Range</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {programs.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={10}
                    className="text-center py-10 text-muted-foreground"
                  >
                    No programs found.
                  </TableCell>
                </TableRow>
              ) : (
                programs.map((program: any, index: number) => (
                  <TableRow key={program._id || index}>
                    <TableCell className="text-muted-foreground text-sm">
                      {(currentPage - 1) * 10 + index + 1}
                    </TableCell>

                    <TableCell>
                      <div className="flex items-center gap-3">
                        {program.coverImageUrl ? (
                          <img
                            src={program.coverImageUrl}
                            alt={program.name}
                            className="h-10 w-14 rounded object-cover shrink-0"
                            onError={(event) => {
                              (
                                event.currentTarget as HTMLImageElement
                              ).style.display = "none";
                            }}
                          />
                        ) : (
                          <div className="h-10 w-14 rounded bg-muted flex items-center justify-center shrink-0">
                            <BookOpen className="h-4 w-4 text-muted-foreground/40" />
                          </div>
                        )}

                        <div>
                          <p className="font-medium leading-tight line-clamp-1">
                            {program.name || "Unnamed"}
                          </p>

                          {program.description && (
                            <p className="text-xs text-muted-foreground line-clamp-1 max-w-[200px]">
                              {program.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </TableCell>

                    <TableCell>
                      {ownerName(program) ? (
                        <div>
                          <p className="text-sm line-clamp-1 max-w-[160px]">{ownerName(program)}</p>
                          {program.organizationName && (
                            <Badge variant="secondary" className="text-xs mt-0.5">
                              Organization
                            </Badge>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>

                    <TableCell>
                      {program.levelName ? (
                        <Badge variant="secondary" className="text-xs">
                          {program.levelName}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>

                    <TableCell>
                      {program.typeName ? (
                        <Badge variant="outline" className="text-xs">
                          {program.typeName}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>

                    <TableCell className="text-sm text-muted-foreground">
                      {program.totalWeeks != null
                        ? `${program.totalWeeks}w`
                        : "—"}
                    </TableCell>

                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {program.startDate || program.endDate ? (
                        <>
                          {program.startDate
                            ? formatDate(program.startDate)
                            : "?"}
                          {" → "}
                          {program.endDate ? formatDate(program.endDate) : "?"}
                        </>
                      ) : (
                        "—"
                      )}
                    </TableCell>

                    <TableCell>
                      <StatusBadge disabled={!!program.disabled} />
                    </TableCell>

                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                      {program.createdAt ? formatDate(program.createdAt) : "—"}
                    </TableCell>

                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>

                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleToggleStatus(program)}>
                            {program.disabled ? (
                              <CheckCircle className="h-4 w-4 mr-2" />
                            ) : (
                              <XCircle className="h-4 w-4 mr-2" />
                            )}
                            {program.disabled ? "Enable" : "Disable"}
                          </DropdownMenuItem>

                          <DropdownMenuSeparator />

                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => setDeleteTarget(program)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete permanently
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <p className="text-sm text-muted-foreground">
            Page {currentPage} of {totalPages} — {totalItems} programs
          </p>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>

            <Button
              variant="outline"
              size="sm"
              disabled={currentPage >= totalPages}
              onClick={() =>
                setCurrentPage((page) => Math.min(totalPages, page + 1))
              }
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editItem ? "Edit Program" : "Add Program"}
            </DialogTitle>
            <DialogDescription>
              {editItem ? "Update program details." : "Create a new program."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label htmlFor="prog-name">Name</Label>
              <Input
                id="prog-name"
                value={form.name}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    name: event.target.value,
                  }))
                }
                placeholder="Program name"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="prog-description">Description</Label>
              <Input
                id="prog-description"
                value={form.description}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    description: event.target.value,
                  }))
                }
                placeholder="Short description"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="prog-status">Status</Label>
              <select
                id="prog-status"
                value={form.status}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    status: event.target.value,
                  }))
                }
                className="w-full border rounded-md px-3 py-2 text-sm bg-background"
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            <div className="space-y-1">
              <Label htmlFor="prog-weeks">Total Weeks</Label>
              <Input
                id="prog-weeks"
                type="number"
                min="0"
                value={form.totalWeeks}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    totalWeeks: event.target.value,
                  }))
                }
                placeholder="e.g. 12"
              />
            </div>

          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>

            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : editItem ? "Save Changes" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open: boolean) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Program Permanently</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete{" "}
              <strong>{deleteTarget?.name}</strong>? This cannot be undone. If
              you just want to hide it, use Disable instead.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>

            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const PLATFORM_DATA_TAB_KEY = "platformDataActiveTab";

const PLATFORM_DATA_TAB_VALUES = [
  "static-data",
  "sports",
  "org-types",
  "packages",
  "programs",
  "plans",
  "storage-plans",
];

const getStoredPlatformDataTab = () => {
  if (typeof window === "undefined") return "static-data";
  const stored = window.localStorage.getItem(PLATFORM_DATA_TAB_KEY);
  return stored && PLATFORM_DATA_TAB_VALUES.includes(stored) ? stored : "static-data";
};

export function StaticDataManager() {
  const [activeTab, setActiveTab] = useState(getStoredPlatformDataTab);

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(PLATFORM_DATA_TAB_KEY, value);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-purple-500 text-white shadow-sm">
          <Settings2 className="h-5 w-5" />
        </span>
        <div>
          <h1 className="text-2xl font-bold">Platform Data</h1>
          <p className="text-muted-foreground">
            View and manage static configuration data, packages, and programs.
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="w-full h-auto flex-wrap justify-start gap-1.5 bg-transparent p-0">
          <TabsTrigger value="static-data" className="gap-1.5 rounded-full border bg-card data-[state=active]:shadow-md">
            <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full ${TAB_ACCENTS["static-data"].badge}`}>
              <Settings2 className="h-3 w-3" />
            </span>
            Static Data
          </TabsTrigger>

          <TabsTrigger value="sports" className="gap-1.5 rounded-full border bg-card data-[state=active]:shadow-md">
            <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full ${TAB_ACCENTS.sports.badge}`}>
              <Dumbbell className="h-3 w-3" />
            </span>
            Sports
          </TabsTrigger>

          <TabsTrigger value="org-types" className="gap-1.5 rounded-full border bg-card data-[state=active]:shadow-md">
            <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full ${TAB_ACCENTS["org-types"].badge}`}>
              <Building2 className="h-3 w-3" />
            </span>
            Org Types
          </TabsTrigger>

          <TabsTrigger value="packages" className="gap-1.5 rounded-full border bg-card data-[state=active]:shadow-md">
            <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full ${TAB_ACCENTS.packages.badge}`}>
              <Package className="h-3 w-3" />
            </span>
            Packages
          </TabsTrigger>

          <TabsTrigger value="programs" className="gap-1.5 rounded-full border bg-card data-[state=active]:shadow-md">
            <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full ${TAB_ACCENTS.programs.badge}`}>
              <BookOpen className="h-3 w-3" />
            </span>
            Programs
          </TabsTrigger>

          <TabsTrigger value="plans" className="gap-1.5 rounded-full border bg-card data-[state=active]:shadow-md">
            <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full ${TAB_ACCENTS.plans.badge}`}>
              <CreditCard className="h-3 w-3" />
            </span>
            Plans
          </TabsTrigger>

          <TabsTrigger value="storage-plans" className="gap-1.5 rounded-full border bg-card data-[state=active]:shadow-md">
            <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full ${TAB_ACCENTS["storage-plans"].badge}`}>
              <HardDrive className="h-3 w-3" />
            </span>
            Storage Plans
          </TabsTrigger>
        </TabsList>

        <div className="mt-6">
          <TabsContent value="static-data">
            <StaticKVTab />
          </TabsContent>

          <TabsContent value="sports">
            <SportsTab />
          </TabsContent>

          <TabsContent value="org-types">
            <OrgTypesTab />
          </TabsContent>

          <TabsContent value="packages">
            <PackagesTab />
          </TabsContent>

          <TabsContent value="programs">
            <ProgramsTab />
          </TabsContent>

          <TabsContent value="plans">
            <PlansTab />
          </TabsContent>

          <TabsContent value="storage-plans">
            <StoragePlansTab />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}